import { AdvancedSearchInfoLink } from 'components/Blotters/advanced-search-info-link';
import { getProductTranslationKey, productFilters } from 'components/Blotters/Filter/BlotterFilterNext.helpers';
import { ToolBarState } from 'components/staticForm/components/dailyBlotter/toolBar/ToolBar.connect';
import { useSearchInputValueDebounce } from 'hooks/useSearchInputValueDebounce';
import { type ChangeEvent, type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { ProductFilter } from 'state/blotters/common/blotter.model';
import { initialDailyBlotterFilters } from 'state/blotters/dailyBlotter/dailyBlotter.model';

import { connectDailyBlotterToolBar } from './ToolBar.connect';

export const ToolBar: FC<ToolBarState> = ({ updateFilters }) => {
  const { inputValue, debouncedInputValue, setInputValue } = useSearchInputValueDebounce(
    initialDailyBlotterFilters.searchedText,
    500,
  );
  const [productFilterValue, setProductFilterValue] = useState<ProductFilter>(initialDailyBlotterFilters.product);
  const { formatMessage } = useIntl();

  const onTextFilterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newInputValue = event.currentTarget.value;
      setInputValue(newInputValue);
    },
    [setInputValue],
  );
  const onProductFilterChanged = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const newProductFilter = e.target.value as ProductFilter;
    setProductFilterValue(newProductFilter);
  }, []);

  useEffect(() => {
    updateFilters({ searchedText: debouncedInputValue, product: productFilterValue });
  }, [debouncedInputValue, productFilterValue, updateFilters]);

  const placeholder = useMemo(() => formatMessage({ id: 'dailyBlotter.placeholder' }), [formatMessage]);

  return (
    <div className="d-flex pb-2 justify-content-between" data-e2e="dailyBlotterToolBar">
      <div id="buttons" className="w-50 flex-grow-1">
        <div className="input-group">
          <span className="input-group-text" id="search-by-keyword">
            <i className="icon">search</i>
          </span>
          <input
            type="search"
            className="form-control w-25"
            placeholder={placeholder}
            aria-label={placeholder}
            aria-describedby="search-by-keyword"
            value={inputValue}
            onChange={onTextFilterChange}
            data-e2e="dailyBlotterSearch"
          />
          <AdvancedSearchInfoLink className="btn-discreet-secondary" />
        </div>
      </div>
      <select
        className="form-select w-25 ms-2"
        onChange={onProductFilterChanged}
        value={productFilterValue}
        data-e2e="dailyBlotterProductSelect"
      >
        {productFilters.map(item => (
          <option key={item} value={item}>
            {formatMessage({ id: getProductTranslationKey(item) })}
          </option>
        ))}
      </select>
    </div>
  );
};

export const DailyBlotterToolBar = connectDailyBlotterToolBar(ToolBar);
DailyBlotterToolBar.displayName = 'DailyBlotterToolBar';
